import { useState, useCallback, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { RefreshCw, Zap, CheckCircle2 } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

/**
 * Botón reusable "Actualizar datos Alegra" — dispara sync incremental.
 * ~2-5 segundos. Se puede colocar en CxC, CxP, Flujo Caja, Alegra Contabilidad.
 *
 * Props:
 * - size: 'sm' (default) | 'default' | 'lg'
 * - variant: 'outline' (default) | 'default' | 'ghost'
 * - onSuccess?: (result) => void  — callback tras éxito, para refrescar la vista
 * - compact?: boolean — si true, solo ícono sin texto
 */
export function AlegraSyncButton({ size = 'sm', variant = 'outline', onSuccess, compact = false }) {
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  const loadStatus = useCallback(async () => {
    try {
      const res = await api.get('/alegra/sync-incremental/status');
      setLastRun(res.data?.last_run || null);
      setLastResult(res.data?.last_result || null);
    } catch { /* sin data aún */ }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const handleClick = async () => {
    setRunning(true);
    try {
      const res = await api.post('/alegra/sync-incremental', {}, { timeout: 90000 });
      const d = res.data;
      if (d.skipped) {
        toast.info(d.reason === 'another_sync_in_progress'
          ? 'Hay otro sync corriendo, espera unos segundos'
          : `Sync omitido: ${d.reason}`);
      } else if (d.error) {
        toast.error(`Error: ${d.error}`);
      } else {
        const inv = d.invoices || {};
        const pay = d.payments || {};
        const total = (inv.imported || 0) + (inv.updated || 0) + (pay.imported || 0) + (pay.updated || 0);
        if (total === 0) {
          toast.success('Ya estás al día — nada nuevo en Alegra');
        } else {
          toast.success(
            `Alegra actualizado: ${inv.imported || 0} facturas nuevas, ${pay.imported || 0} pagos nuevos`
          );
        }
        setLastRun(new Date().toISOString());
        setLastResult(d);
        if (onSuccess) onSuccess(d);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error actualizando Alegra');
    } finally {
      setRunning(false);
    }
  };

  // Texto del tooltip con último sync
  const lastText = lastRun
    ? `Último: ${new Date(lastRun).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}`
    : 'Nunca';

  if (compact) {
    return (
      <Button
        size={size}
        variant={variant}
        onClick={handleClick}
        disabled={running}
        title={`Actualizar datos desde Alegra (${lastText})`}
        data-testid="btn-alegra-sync-incremental"
      >
        <RefreshCw className={`w-4 h-4 ${running ? 'animate-spin' : ''}`} />
      </Button>
    );
  }

  return (
    <Button
      size={size}
      variant={variant}
      onClick={handleClick}
      disabled={running}
      title={lastText}
      data-testid="btn-alegra-sync-incremental"
      className="gap-1.5"
    >
      {running ? (
        <>
          <RefreshCw className="w-4 h-4 animate-spin" />
          <span>Actualizando...</span>
        </>
      ) : (
        <>
          <Zap className="w-4 h-4 text-amber-500" />
          <span>Actualizar Alegra</span>
          {lastResult && (
            <Badge variant="secondary" className="ml-1 text-[10px] h-4 px-1">
              <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" />
              {lastText.replace('Último: ', '')}
            </Badge>
          )}
        </>
      )}
    </Button>
  );
}

export default AlegraSyncButton;
